/**
 * Base Web Component Class
 * Foundation for all Khaime SDK components with Shadow DOM isolation
 */

import type { SDKConfig } from '../types';
import { DEFAULT_CONFIG } from '../types';
import { ApiClient, getApiClient } from '../utils/api-client';
import { applyConfigToHost } from '../utils/css-vars';
import { createKhaimeEvent } from '../types/events';

/**
 * Base styles applied to all components
 */
export const BASE_STYLES = `
  :host {
    display: block;
    font-family: var(--ks-font-family, Inter, system-ui, -apple-system, sans-serif);
    font-size: var(--ks-body-size, 1rem);
    color: var(--ks-text, #111827);
    line-height: 1.5;
    box-sizing: border-box;
  }

  :host([hidden]) {
    display: none;
  }

  *,
  *::before,
  *::after {
    box-sizing: inherit;
  }

  /* Loading spinner */
  .ks-spinner {
    width: 24px;
    height: 24px;
    border: 2px solid var(--ks-border, #e5e7eb);
    border-top-color: var(--ks-primary, #6366F1);
    border-radius: 50%;
    animation: ks-spin 0.8s linear infinite;
  }

  @keyframes ks-spin {
    to { transform: rotate(360deg); }
  }

  /* Loading container */
  .ks-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    min-height: 200px;
  }

  /* Skeleton shimmer */
  .ks-skeleton {
    background: var(--ks-surface, #f3f4f6);
    border-radius: var(--ks-radius, 0.5rem);
    position: relative;
    overflow: hidden;
  }

  .ks-skeleton::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.4) 50%,
      transparent 100%
    );
    animation: ks-shimmer 1.5s ease-in-out infinite;
  }

  :host([data-ks-mode="dark"]) .ks-skeleton {
    background: var(--ks-surface, #1e293b);
  }

  :host([data-ks-mode="dark"]) .ks-skeleton::after {
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.06) 50%,
      transparent 100%
    );
  }

  @keyframes ks-shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }

  .ks-skeleton-text {
    height: 14px;
    margin-bottom: 8px;
    border-radius: 4px;
  }

  .ks-skeleton-text--sm {
    height: 10px;
    width: 60%;
  }

  .ks-skeleton-text--lg {
    height: 20px;
    width: 80%;
  }

  /* Error state */
  .ks-error {
    padding: 1rem;
    color: #dc2626;
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: var(--ks-radius, 0.5rem);
    text-align: center;
  }

  /* Empty state */
  .ks-empty {
    padding: 2rem;
    text-align: center;
    color: var(--ks-text-muted, #6B7280);
  }

  /* Transitions */
  .ks-transition {
    transition: all var(--ks-transition, 300ms) ease;
  }

  /* Hover animations */
  [data-ks-hover="lift"]:hover {
    transform: translateY(-4px);
  }

  [data-ks-hover="scale"]:hover {
    transform: scale(1.02);
  }

  [data-ks-hover="glow"]:hover {
    box-shadow: 0 0 20px rgba(99, 102, 241, 0.3);
  }

  /* Card styles */
  .ks-card--flat {
    background: transparent;
  }

  .ks-card--elevated {
    background: var(--ks-surface, #F9FAFB);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06);
  }

  .ks-card--outlined {
    background: var(--ks-surface, #F9FAFB);
    border: 1px solid var(--ks-border, #E5E7EB);
  }

  .ks-card--glass {
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.2);
  }

  /* Dark mode overrides */
  :host([data-ks-mode="dark"]) {
    --ks-background: #0f172a;
    --ks-surface: #1e293b;
    --ks-text: #f8fafc;
    --ks-text-muted: #94a3b8;
    --ks-border: #334155;
  }

  :host([data-ks-mode="dark"]) .ks-card--glass {
    background: rgba(30, 41, 59, 0.8);
    border-color: rgba(51, 65, 85, 0.5);
  }

  /* Buttons */
  .ks-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.75rem 1.5rem;
    font-size: 0.875rem;
    font-weight: 600;
    border: none;
    border-radius: var(--ks-radius, 0.5rem);
    cursor: pointer;
    transition: all var(--ks-transition, 300ms) ease;
  }

  .ks-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .ks-button--primary {
    background: var(--ks-primary, #6366F1);
    color: white;
  }

  .ks-button--primary:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  .ks-button--secondary {
    background: var(--ks-surface, #F9FAFB);
    color: var(--ks-text, #111827);
    border: 1px solid var(--ks-border, #E5E7EB);
  }

  .ks-button--secondary:hover:not(:disabled) {
    background: var(--ks-border, #E5E7EB);
  }

  .ks-button--outline {
    background: transparent;
    color: var(--ks-primary, #6366F1);
    border: 2px solid var(--ks-primary, #6366F1);
  }

  .ks-button--outline:hover:not(:disabled) {
    background: var(--ks-primary, #6366F1);
    color: white;
  }
`;

/**
 * Abstract base class for all Khaime Web Components
 */
export abstract class KhaimeBaseElement extends HTMLElement {
  protected shadow: ShadowRoot;
  protected config: SDKConfig = DEFAULT_CONFIG;
  protected api: ApiClient | null = null;
  protected _initialized = false;
  protected _loading = true;
  protected _error: string | null = null;
  /** External config overrides passed programmatically (e.g. from WordPress plugin) */
  private _configOverrides: Partial<SDKConfig> | null = null;

  /**
   * Observed attributes that trigger re-render when changed
   */
  static get observedAttributes(): string[] {
    return [
      'merchant-id',
      'sandbox',
      'theme-mode',
      'primary-color',
      'columns',
      'card-style',
    ];
  }

  constructor() {
    super();
    // Create Shadow DOM with open mode (allows inspection)
    this.shadow = this.attachShadow({ mode: 'open' });
  }

  /**
   * Called when element is added to DOM
   */
  async connectedCallback(): Promise<void> {
    if (this._initialized) return;
    this._initialized = true;

    // Inject base styles
    this.injectStyles(BASE_STYLES);

    // Show loading state
    this.renderLoading();

    try {
      // Initialize API client
      await this.initializeApi();

      // Load config
      await this.loadConfig();

      // Apply theme
      applyConfigToHost(this, this.config);

      // Render component
      this._loading = false;
      await this.render();

      // Set up event listeners
      this.setupEventListeners();

      // Emit ready event
      this.dispatchEvent(
        createKhaimeEvent('khaime:config-loaded', {
          config: this.config as unknown as Record<string, unknown>,
          source: 'api',
          merchantId: this.merchantId,
        })
      );
    } catch (error) {
      this._error = error instanceof Error ? error.message : 'Unknown error';
      this.renderError(this._error);
    }
  }

  /**
   * Called when element is removed from DOM
   */
  disconnectedCallback(): void {
    this.cleanup();
  }

  /**
   * Called when observed attributes change
   */
  attributeChangedCallback(
    name: string,
    oldValue: string | null,
    newValue: string | null
  ): void {
    if (oldValue === newValue) return;
    if (!this._initialized) return;

    this.handleAttributeChange(name, newValue);
  }

  /**
   * Get merchant ID from attribute
   */
  get merchantId(): string {
    return this.getAttribute('merchant-id') || '';
  }

  /**
   * Get sandbox mode from attribute
   */
  get sandbox(): boolean {
    return this.hasAttribute('sandbox');
  }

  /**
   * Initialize API client
   */
  protected async initializeApi(): Promise<void> {
    const merchantId = this.merchantId;
    if (!merchantId) {
      throw new Error('merchant-id attribute is required');
    }

    this.api = getApiClient({
      merchantId,
      sandbox: this.sandbox,
      debug: this.hasAttribute('debug'),
    });
  }

  /**
   * Set config overrides from programmatic usage (e.g. WordPress plugin).
   * Must be called BEFORE the element is added to the DOM (connectedCallback).
   */
  public setConfigOverrides(overrides: Partial<SDKConfig>): void {
    this._configOverrides = overrides;
  }

  /**
   * Load SDK configuration from API
   */
  protected async loadConfig(): Promise<void> {
    if (!this.api) return;

    try {
      const config = await this.api.getConfig(this.getSDKType());
      this.config = { ...DEFAULT_CONFIG, ...config };
    } catch {
      // Use defaults if config fetch fails
      this.config = DEFAULT_CONFIG;
    }

    // Apply programmatic overrides (from WordPress plugin / static methods)
    if (this._configOverrides) {
      this.applyProgrammaticOverrides(this._configOverrides);
    }

    // Apply attribute overrides (highest priority)
    this.applyAttributeOverrides();
  }

  /**
   * Get SDK type for this component
   */
  protected getSDKType(): string {
    return 'storefront';
  }

  /**
   * Deep-merge programmatic config overrides onto the current config.
   * Only overrides values that were explicitly provided.
   */
  private applyProgrammaticOverrides(overrides: Partial<SDKConfig>): void {
    if (overrides.theme) {
      if (overrides.theme.mode) this.config.theme.mode = overrides.theme.mode;
      if (overrides.theme.borderRadius) this.config.theme.borderRadius = overrides.theme.borderRadius;
      if (overrides.theme.colors) {
        this.config.theme.colors = { ...this.config.theme.colors, ...overrides.theme.colors };
      }
    }
    if (overrides.layout) {
      if (overrides.layout.style) this.config.layout.style = overrides.layout.style;
      if (overrides.layout.columns) this.config.layout.columns = overrides.layout.columns;
      if (overrides.layout.spacing) this.config.layout.spacing = overrides.layout.spacing;
      if (overrides.layout.cardStyle) this.config.layout.cardStyle = overrides.layout.cardStyle;
    }
    if (overrides.effects) {
      if (overrides.effects.hoverAnimation) this.config.effects.hoverAnimation = overrides.effects.hoverAnimation;
      if (overrides.effects.transitions) this.config.effects.transitions = overrides.effects.transitions;
    }
    if (overrides.features) {
      if (overrides.features.showPrices !== undefined) this.config.features.showPrices = overrides.features.showPrices;
      if (overrides.features.showRatings !== undefined) this.config.features.showRatings = overrides.features.showRatings;
      if (overrides.features.showDescription !== undefined) this.config.features.showDescription = overrides.features.showDescription;
      if (overrides.features.quickBuy !== undefined) this.config.features.quickBuy = overrides.features.quickBuy;
      if (overrides.features.showBadges !== undefined) this.config.features.showBadges = overrides.features.showBadges;
    }
    if (overrides.typography) {
      if (overrides.typography.fontFamily) this.config.typography.fontFamily = overrides.typography.fontFamily;
      if (overrides.typography.headingWeight) this.config.typography.headingWeight = overrides.typography.headingWeight;
      if (overrides.typography.bodySize) this.config.typography.bodySize = overrides.typography.bodySize;
    }
  }

  /**
   * Apply attribute overrides to config
   */
  protected applyAttributeOverrides(): void {
    // Theme mode
    const mode = this.getAttribute('theme-mode');
    if (mode === 'light' || mode === 'dark') {
      this.config.theme.mode = mode;
    }

    // Primary color
    const primaryColor = this.getAttribute('primary-color');
    if (primaryColor) {
      this.config.theme.colors.primary = primaryColor;
    }

    // Columns
    const columns = this.getAttribute('columns');
    if (columns) {
      const num = parseInt(columns, 10);
      if ([1, 2, 3, 4].includes(num)) {
        this.config.layout.columns = num as 1 | 2 | 3 | 4;
      }
    }

    // Card style
    const cardStyle = this.getAttribute('card-style');
    if (cardStyle && ['flat', 'elevated', 'outlined', 'glass'].includes(cardStyle)) {
      this.config.layout.cardStyle = cardStyle as any;
    }
  }

  /**
   * Inject CSS into Shadow DOM
   */
  protected injectStyles(css: string): void {
    const style = document.createElement('style');
    style.textContent = css;
    this.shadow.appendChild(style);
  }

  /**
   * Render loading state
   */
  protected renderLoading(): void {
    const container = this.shadow.querySelector('.ks-container');
    if (container) {
      container.innerHTML = `
        <div class="ks-loading">
          <div class="ks-spinner"></div>
        </div>
      `;
    } else {
      this.shadow.innerHTML += `
        <div class="ks-container">
          <div class="ks-loading">
            <div class="ks-spinner"></div>
          </div>
        </div>
      `;
    }
  }

  /**
   * Render error state
   */
  protected renderError(message: string): void {
    const container = this.shadow.querySelector('.ks-container');
    const html = `
      <div class="ks-error">
        <p>${message}</p>
        <button class="ks-button ks-button--secondary" onclick="this.getRootNode().host.retry()">
          Retry
        </button>
      </div>
    `;

    if (container) {
      container.innerHTML = html;
    } else {
      this.shadow.innerHTML += `<div class="ks-container">${html}</div>`;
    }
  }

  /**
   * Retry loading after error
   */
  public async retry(): Promise<void> {
    this._error = null;
    this._initialized = false;
    // Clear shadow DOM except styles
    const styles = this.shadow.querySelectorAll('style');
    this.shadow.innerHTML = '';
    styles.forEach((style) => this.shadow.appendChild(style));

    await this.connectedCallback();
  }

  /**
   * Refresh data
   */
  public async refresh(): Promise<void> {
    this._loading = true;
    this.renderLoading();
    await this.render();
    this._loading = false;
  }

  /**
   * Handle attribute changes - to be overridden by subclasses
   */
  protected handleAttributeChange(name: string, value: string | null): void {
    // Default: trigger re-render
    this.applyAttributeOverrides();
    applyConfigToHost(this, this.config);
    this.render();
  }

  /**
   * Abstract: Render component content
   */
  protected abstract render(): void | Promise<void>;

  /**
   * Set up event listeners - to be overridden by subclasses
   */
  protected setupEventListeners(): void {}

  /**
   * Cleanup - to be overridden by subclasses
   */
  protected cleanup(): void {}
}

/**
 * Registry for custom elements
 */
export class ComponentRegistry {
  private static registered = new Set<string>();

  /**
   * Define a custom element only once
   */
  static define(tagName: string, constructor: CustomElementConstructor): void {
    if (this.registered.has(tagName)) return;
    if (customElements.get(tagName)) return;

    customElements.define(tagName, constructor);
    this.registered.add(tagName);
  }

  /**
   * Check if a component is registered
   */
  static isRegistered(tagName: string): boolean {
    return this.registered.has(tagName) || !!customElements.get(tagName);
  }

  /**
   * Wait for a component to be defined
   */
  static whenDefined(tagName: string): Promise<CustomElementConstructor> {
    return customElements.whenDefined(tagName);
  }
}
